import React, { useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import dayjs from 'dayjs';
import { Link } from '@firsttable/web-components/atoms';
import parse, { domToReact } from 'html-react-parser';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isBetween from 'dayjs/plugin/isBetween';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import { useLocalStorage } from 'usehooks-ts';
import { TrackProvider } from '@components/analytics/context/AnalyticsContext';
import { useAuthState } from '../../context/AuthContext';
// import SilverstripeContext from '../../context/Silverstripe';
import NavLink from '../../GatsbyLinkConvertor';
import { NEXT_PUBLIC_DEFAULT_TZ } from '../../../lib/env';

const AlertBannerComponent = dynamic(
  () => import('@firsttable/web-components/molecules/AlertBanner')
);

dayjs.extend(utc);
dayjs.extend(timezone); // dependent on utc plugin
dayjs.extend(isBetween);

const parseAlertMessage: any = (content) =>
  parse(content, {
    replace(node: any) {
      const { children, name: tagName } = node;
      switch (tagName) {
        case 'a': {
          return (
            <TrackProvider
              link_copy={typeof children === 'string' ? children : undefined}
              link_name="alert message link">
              <Link NavComponent={NavLink} href={node.attribs.href} underline>
                {domToReact(children)}
              </Link>
            </TrackProvider>
          );
        }
        default:
          return node;
      }
    },
  });

const nz = 1;
const aus = 2;
const au = 2;
const canada = 4;
const ca = 4;
const uk = 8;
const ireland = 11;
const ie = 11;
const indonesia = 12;
const id = 12;

const TIMEOUT_DURATION = 1500;

export const GET_ALERT_STATUS = gql`
  query getAlertStatus($siteId: Int!) {
    AlertMessage(siteId: $siteId) {
      alertMessage
      alertType
      showUntil
    }
    MemberAlert {
      hasMarkedAsSpam
      hasBouncedEmail
    }
  }
`;

const GET_ALERT_BANNER = gql`
  query getAlertBanner($siteId: Int!, $id: Int!) {
    AlertBanner(siteId: $siteId, id: $id) {
      id
      title
      content
      start
      end
      priority
      published
      displayType
      excludePages {
        id
        slug
      }
      includePages {
        id
        slug
      }
    }
  }
`;

const siteId = +process?.env?.NEXT_PUBLIC_SITE_ID || 0;
const isSite = (site: number | number[]): boolean => [].concat(site).includes(siteId);
const notSite = (site: number | number[]): boolean => !isSite(site);

const AppLink = () => (
  <>
    <TrackProvider link_name="ios appstore">
      <Link
        NavComponent={NavLink}
        to="https://apps.apple.com/app/apple-store/id1599994101?pt=121303410&ct=website%20banner%20referral&mt=8"
        target="_blank"
        underline>
        iOS
      </Link>
    </TrackProvider>{' '}
    or{' '}
    <TrackProvider link_name="android play store">
      <Link
        NavComponent={NavLink}
        to="https://play.google.com/store/apps/details?id=com.firsttable.live&referrer=utm_source%3Dwebsite_banner%26utm_medium%3Dwebsite%26utm_campaign%3Dnone"
        target="_blank"
        underline>
        Android
      </Link>
    </TrackProvider>
  </>
);

const HoldingBanner = () => {
  const message = (
    <>
      Book faster on the First Table <AppLink /> app 📱
    </>
  );
  const messageCA = (
    <>
      Making travel plans?{' '}
      <TrackProvider link_name="first table around the world">
        <Link NavComponent={NavLink} to="https://firsttable.com" target="_blank" underline>
          Discover First Table
        </Link>
      </TrackProvider>{' '}
      in other countries around the world 🌏
    </>
  );
  return (
    <AlertBannerComponent opacity={1} p={undefined} message={isSite(ca) ? messageCA : message} />
  );
};

const AlertBanner = ({ preview, siteConfig }) => {
  const [bannerHeight, setBannerHeight] = useState(0);
  const { asPath, pathname, query, push: router, events } = useRouter();
  const inPath = (path) => asPath && asPath.includes(path);
  const auth = useAuthState();
  // const { setCuisineByValue } = useRestaurantSearchState();

  const { data: previewBanner, loading: previewBannerLoading } = useQuery(GET_ALERT_BANNER, {
    skip: !query?.['preview-alert-banner'],
    variables: {
      siteId,
      id: +query?.['preview-alert-banner'],
    },
    fetchPolicy: 'network-only',
  });
  const [memberAlertCheckTime, setMemberAlertCheckTime] = useLocalStorage<null | number>(
    'firsttable.memberAlertCheckTime',
    null
  );

  // store last updated on member record
  const { data } = useQuery(GET_ALERT_STATUS, {
    // pollInterval: 60000, // every minute
    ssr: false,
    fetchPolicy: 'network-only',
    variables: {
      siteId,
    },
    skip:
      (memberAlertCheckTime && memberAlertCheckTime > dayjs().subtract(10, 'minute').unix()) ||
      !auth.isLoggedIn,
    onCompleted: (d) => {
      setMemberAlertCheckTime(dayjs().unix());
      if (d?.AlertMessage.alertMessage) {
        const timer = setTimeout(() => {
          setBannerHeight(50);
        }, TIMEOUT_DURATION);
        return () => {
          clearTimeout(timer);
        };
      }
    },
  });

  if (pathname === '/dine-now') {
    return null;
  }

  const tz = siteConfig.defaultTimeZone || NEXT_PUBLIC_DEFAULT_TZ || 'UTC';
  const currentDate = dayjs().tz(tz); // .format('DD/MM/YYYY');
  const isDate = (date: string | string[]): boolean =>
    [].concat(date).includes(dayjs().tz(tz).format('YYYY-MM-DD'));
  const season = () =>
    /* XOR - Will work ou current summer/winter season. Flipped for southern hemisphere */
    [4, 5, 6, 7, 8, 9].includes(+dayjs().tz(tz).format('M')) === [nz, au].includes(siteId)
      ? 'winter'
      : 'summer';

  // eslint-disable-next-line prefer-const
  let showHoldingBanner = false;

  if (data) {
    const { AlertMessage, MemberAlert } = data;
    const showMessageTill = AlertMessage.showUntil
      ? dayjs(AlertMessage.showUntil).tz(tz) // .format('DD/MM/YYYY')
      : null;

    if (MemberAlert.hasMarkedAsSpam || MemberAlert.hasBouncedEmail) {
      const message = (
        <>
          Hi there. We tried emailing you, but we had some troubles as our email was bounced or
          marked as spam. Please&nbsp;{' '}
          <TrackProvider link_name="retry bounced email">
            <Link NavComponent={NavLink} to="/profile/emails" underline>
              click here
            </Link>
          </TrackProvider>{' '}
          &nbsp;if you’d like us to retry
        </>
      );
      return <AlertBannerComponent message={message} type="danger" />;
    }
    if (AlertMessage.alertMessage && (!showMessageTill || showMessageTill > currentDate)) {
      return (
        <AlertBannerComponent
          height={bannerHeight}
          opacity={bannerHeight > 0 ? 1 : 0}
          p={bannerHeight > 0 ? undefined : 0}
          message={parseAlertMessage(AlertMessage.alertMessage)}
          type={AlertMessage.alertType}
        />
      );
    }
  }

  if (preview) {
    return (
      <AlertBannerComponent
        opacity={1}
        p={undefined}
        message={
          <>
            {/* This API route clears the preview cookies via a document request. */}
            {'Preview Mode | '}
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
            <a href="/api/exit-preview">Exit Preview</a>
          </>
        }
      />
    );
  }

  // if (isSite([uk, ie])) {
  //   const message = (
  //     <>
  //       ❤️ Mum&apos;s the word:{' '}
  //       <TrackProvider link_name='gift vouchers'>
  //         <Link NavComponent={NavLink} to="/gift-vouchers/purchase" underline>
  //           10% off First Table gift vouchers
  //         </Link>
  //       </TrackProvider>{' '}
  //         🎁
  //     </>
  //   );
  //   return <AlertBannerComponent opacity={1} p={undefined} message={message} type="success" />;
  // }

  // return null;

  const blackFridayDate = ['2022-11-25', '2022-11-26', '2022-11-27'];
  if (blackFridayDate.includes(currentDate.format('YYYY-MM-DD'))) {
    const message = (
      <>
        🔥 Black Friday: half-price bookings in the First Table app! Download for <AppLink /> 🥳
      </>
    );
    const messageCA = (
      <>🔥 Black Friday sale: all First Table service fees half price until Sunday 🥳</>
    );
    return (
      <AlertBannerComponent
        opacity={1}
        p={undefined}
        message={isSite(ca) ? messageCA : message}
        type="success"
      />
    );
  }

  const cyberMondayDate = dayjs('2022-11-28');
  const GVLink = '/gift-vouchers/purchase';
  const CreditLink = '/top-up';

  const cyberMondayLink = CreditLink;
  if (
    // (currentDate.format('YYYY-MM-DD') === blackFridayDate.format('2021-11-26') ||
    // currentDate.format('YYYY-MM-DD') === blackFridayDate.format('2021-11-27') ||
    // currentDate.format('YYYY-MM-DD') === blackFridayDate.format('2021-11-28')
    //  currentDate.format('YYYY-MM-DD') === blackFridayDate.format('2021-11-29')) &&
    // currentDate.isBetween(blackFridayDate, cyberMondayDate, 'day') &&
    // isSite([nz, au, uk, ca, ie])
    //! inPath(cyberMondayLink) &&
    currentDate.format('YYYY-MM-DD') === cyberMondayDate.format('YYYY-MM-DD')
  ) {
    //   console.log('CM');

    const alertMessage = (
      <>
        {' '}
        🔥 Cyber Monday Sale:{' '}
        <TrackProvider link_name="cyber monday sale">
          <Link NavComponent={NavLink} to={cyberMondayLink} underline>
            20% off First Table credit, today only
          </Link>
        </TrackProvider>{' '}
        🥳 🔥
      </>
    );
    return <AlertBannerComponent opacity={1} p={undefined} message={alertMessage} type="success" />;
  }

  const christmasDate = dayjs('2022-12-25');

  // if the current date is before christmasDate
  if (currentDate.isBefore(christmasDate.tz(tz))) {
    const message = (
      <>
        🎅 Looking for the perfect stocking filler? First Table Christmas{' '}
        <TrackProvider link_name="christmas sale">
          <Link NavComponent={NavLink} to="/gift-vouchers/purchase" target="_blank" underline>
            gift vouchers
          </Link>
        </TrackProvider>{' '}
        are now 10% off 🥂
      </>
    );
    return <AlertBannerComponent opacity={1} p={undefined} message={message} type="success" />;
  }

  // if it's Christmas day
  if (currentDate.format('YYYY-MM-DD') === christmasDate.format('YYYY-MM-DD')) {
    const message = (
      <>🎄 Merry Christmas from First Table! Check back tomorrow for our Boxing Day sale 🙌</>
    );
    return <AlertBannerComponent opacity={1} p={undefined} message={message} type="success" />;
  }

  const showBoxingDayBannerOn = [
    '2022-12-26',
    '2022-12-27',
    '2022-12-28',
    '2022-12-29',
    '2022-12-30',
    '2022-12-31',
  ];

  if (showBoxingDayBannerOn.includes(currentDate.format('YYYY-MM-DD'))) {
    const alertMessage = (
      <div style={{ textAlign: 'center' }}>
        🥂 Stock up for the year ahead with{' '}
        <TrackProvider link_name="boxing day sale">
          <Link NavComponent={NavLink} to="/top-up/new" underline>
            25% off First Table credit for Boxing Day
          </Link>
        </TrackProvider>
        ! 🙌
      </div>
    );

    return <AlertBannerComponent opacity={1} p={undefined} message={alertMessage} type="success" />;
  }

  // if (isSite([nz])) {
  //   const message = (
  //     <>
  //       🚀 Exciting opportunity:{' '}
  //       <Link
  //         NavComponent={NavLink}
  //         to="https://www.snowballeffect.co.nz/offers/first-table-kb6e6"
  //         target="_blank"
  //         underline>
  //         Invest in First Table
  //       </Link>{' '}
  //       and share in our success! 🚀
  //     </>
  //   );

  //   return <AlertBannerComponent opacity={1} p={undefined} message={message} type="success" />;
  // }

  /*
  if (inPath('queenstown-lakes') || inPath('auckland') || inPath('christchurch')) {
    let city = '';
    if (inPath('queenstown-lakes')) {
      city = 'Queenstown';
    } else if (inPath('auckland')) {
      city = 'Auckland';
    } else if (inPath('christchurch')) {
      city = 'Christchurch';
    }
    const message = (
      <>
        🎉 {city}, meet{' '}
        <Link NavComponent={NavLink} to="/magazine/regular-table" underline>
          Regular Table
        </Link>
        ! 🥳
      </>
    );
    return <AlertBannerComponent opacity={1} p={undefined} message={message} type="success" />;
  }
   */

  // if (isSite(au) && inPath('/sydney')) {
  //   const alertMessage = (
  //     <div style={{ textAlign: 'center' }}>
  //       Book for four in Sydney and {` `}
  //       <Link NavComponent={NavLink} to="/competitions/book-for-four-and-win" underline>
  //         be in to WIN
  //       </Link>
  //     </div>
  //   );

  //   return <AlertBannerComponent opacity={1} p={undefined} message={alertMessage} type="success" />;
  // }

  // Friday frenzy
  const fridayFrenzyDate = '2023-07-28';
  const isFridayFrenzy = isDate(fridayFrenzyDate);
  const isTargetSite = isSite([uk, ie, ca, au]);

  if (isTargetSite && isFridayFrenzy) {
    const message = <>🔥 Friday Frenzy: Half-price bookings, till midnight 🥳</>;
    return <AlertBannerComponent opacity={1} p={undefined} message={message} type="success" />;
  }

  // NZ Double Points
  const FFPromoNZ = ['2023-07-28', '2023-07-29', '2023-07-30'];
  const isFFPromoNZDate = isDate(FFPromoNZ);
  const isFFPromoNZSite = isSite([nz]);
  showHoldingBanner = isFFPromoNZSite || isTargetSite;
  showHoldingBanner = false;

  if (isFFPromoNZDate && isFFPromoNZSite) {
    const message = (
      <>
        Double{' '}
        <TrackProvider link_name="double frequent foodies">
          <Link NavComponent={NavLink} to="/frequent-foodies/welcome" underline>
            Frequent Foodies®
          </Link>
        </TrackProvider>{' '}
        points for all bookings, until Sunday! 🥳
      </>
    );
    return <AlertBannerComponent opacity={1} p={undefined} message={message} type="success" />;
  }

  // GV sale
  const GVSaleDate = [
    '2023-06-13',
    '2023-06-14',
    '2023-06-15',
    '2023-06-16',
    '2023-06-17',
    '2023-06-18',
  ];
  const isGVSaleDate = GVSaleDate.includes(currentDate.tz(tz).format('YYYY-MM-DD'));
  const isTargetGVSite = isSite([ca, uk, ie]);

  if (isGVSaleDate && isTargetGVSite) {
    const message = (
      <>
        🥳 Save 25% on all Father&apos;s Day{' '}
        <TrackProvider link_name="fathers day sale">
          <Link
            NavComponent={NavLink}
            to="/gift-vouchers/purchase?theme=Father%27s%20Day"
            underline>
            gift vouchers
          </Link>
        </TrackProvider>{' '}
        until Sunday 🎁
      </>
    );
    return <AlertBannerComponent opacity={1} p={undefined} message={message} type="success" />;
  }

  if (previewBanner?.AlertBanner || siteConfig?.alertBanners?.length) {
    showHoldingBanner = true;
    const activeBanner =
      previewBanner?.AlertBanner ||
      siteConfig.alertBanners
        .sort((a, b) => a.priority - b.priority)
        .find(
          (alert) =>
            (!alert?.start || currentDate >= dayjs.tz(alert.start, tz)) && // Today is equal or after start
            (!alert?.end || currentDate <= dayjs.tz(alert.end, tz)) && // Today is equal or before end
            (!alert?.includePages.length ||
              alert.includePages.some(({ slug }) => inPath(`${slug}`))) && // Current path is in target list
            (!alert?.excludePages.length ||
              alert.excludePages.every(({ slug }) => !inPath(`${slug}`))) // Current path is no on exclude target
        );

    let previewBannerStatus = null;
    if (previewBanner) {
      const match = (siteConfig?.alertBanners ?? []).find(
        ({ id: bannerId }) => +bannerId === +previewBanner?.AlertBanner?.id
      );
      let status = previewBanner.AlertBanner.published ? 'published not deployed' : 'draft';

      if (match) {
        const same =
          match.content === previewBanner.AlertBanner.content &&
          match.displayType === previewBanner.AlertBanner.displayType &&
          match.priority === previewBanner.AlertBanner.priority &&
          match.start === previewBanner.AlertBanner.start &&
          match.end === previewBanner.AlertBanner.end &&
          JSON.stringify(match.includePages) ===
            JSON.stringify(previewBanner.AlertBanner.includePages) &&
          JSON.stringify(match.excludePages) ===
            JSON.stringify(previewBanner.AlertBanner.excludePages);

        status = same ? 'published and deployed' : 'deployed, but outdated';
      }
      const message = (
        <>
          Status: {status} | Start Date: {previewBanner.AlertBanner.start} | End Date:{' '}
          {previewBanner.AlertBanner.end}
        </>
      );
      previewBannerStatus = <AlertBannerComponent opacity={1} p={undefined} message={message} />;
    }

    if (activeBanner) {
      return (
        <>
          <AlertBannerComponent
            opacity={1}
            p={undefined}
            message={parseAlertMessage(activeBanner.content)}
            type={activeBanner.displayType.toLowerCase()}
          />
          {previewBannerStatus}
        </>
      );
    }
  }

  return (showHoldingBanner && <HoldingBanner />) || null;
};

export default AlertBanner;

AlertBanner.propTypes = {
  preview: PropTypes.bool,
};
